"use client";

import React, { useMemo } from "react";
import {
  useGlobalModalStack,
  MODAL_IDS,
} from "@west-tennessee-consulting/ui";
import { useSearchParams } from "next/navigation";
import { UniversalDetailModalContainer } from "../../UniversalDetailModalContainer";
import { ExportScopeModal } from "../../modals/ExportScopeModal";
import { AskMeridianModalContainer } from "../../UniversalDetailPage/components/AskMeridianModal";
import { ResearchCompanyModal } from "@/features/companies/components/ResearchCompanyModal";
import { FindDecisionMakersModal } from "@/features/companies/components/FindDecisionMakersModal";
import { LogActivityModal } from "@/features/contacts/components/LogActivityModal";
import { LinkCompanyModalWrapper } from "@/features/contacts/components/LinkCompanyModalWrapper";
import { ContactConsolidationModalWrapper } from "@/features/contacts/components/ContactConsolidationModalWrapper";
import { BulkEmailExportModal } from "../../BulkActions/BulkEmailExportModal";
import { LinkContactModalWrapper } from "@/features/companies/components/LinkContactModalWrapper";
import { CompanyConsolidationModalWrapper } from "@/features/companies/components/CompanyConsolidationModalWrapper";
import { SolutionEngineeringModal } from "@/features/offerings/components/SolutionEngineeringModal";
import { ReassignCommunicationSalesAttributionModal } from "@/features/tasks/components/ReassignCommunicationSalesAttributionModal";
import { SetDueDateModal } from "@/components/shared/modals/SetDueDateModal";
import type { BaseEntity } from "../../UniversalCRMTable/types";
import { useUniversalEntityPageContext } from "../contexts/UniversalEntityPageContext";
import { useDetailModalNavigation } from "../hooks/useDetailModalNavigation";
import { EntityCreateModals } from "./EntityPageModals/EntityCreateModals";
import { EntityEditDeleteModals } from "./EntityPageModals/EntityEditDeleteModals";

export function EntityPageModals<T extends BaseEntity>() {
  const context = useUniversalEntityPageContext<T>();
  const globalModalStack = useGlobalModalStack();
  const searchParams = useSearchParams();
  const { itemIds, currentItemIndex } = useDetailModalNavigation<T>();

  const { entityName, config, hooks, entityFormProps, customComponents } = context.pageConfig;
  const { editingItem, showComposeModal, setShowComposeModal, showExportModal, setShowExportModal } =
    context.entityState;
  const {
    handleItemCreated,
    handleItemUpdated,
    handleItemDeleted,
    handleCancelDelete,
    handleExportWithScope,
  } = context.handlers;
  const { deleteMutation } = context.mutations;
  const { refetch, data } = context.dataState;
  const { isExporting, exportProgress } = context.exportState;
  const { currentFilters, advancedFilters, debouncedSearchTerm } = context.filterState;

  const currentPageCount = useMemo(() => data?.data?.length || 0, [data?.data?.length]);
  const totalCount = useMemo(() => data?.totalCount || 0, [data?.totalCount]);
  const hasActiveFilters = useMemo(
    () =>
      Object.keys(currentFilters).length > 0 ||
      Object.keys(advancedFilters).length > 0 ||
      debouncedSearchTerm.length > 0,
    [currentFilters, advancedFilters, debouncedSearchTerm]
  );

  const contextContact = useMemo(() => {
    if (entityName !== "contacts") return undefined;
    const contactIdFromUrl = searchParams?.get("contactsId");
    if (!contactIdFromUrl) return undefined;
    const rows = (data?.data ?? []) as Array<{
      id: string;
      company_id?: string | null;
      company_name?: string | null;
    }>;
    const matchedContact = rows.find((row) => row.id === contactIdFromUrl);
    return matchedContact ? { id: matchedContact.id, company_id: matchedContact.company_id ?? null, company_name: matchedContact.company_name ?? null } : { id: contactIdFromUrl };
  }, [entityName, searchParams, data?.data]);

  return (
    <>
      <EntityCreateModals
        entityName={entityName}
        config={config}
        globalModalStack={globalModalStack}
        entityFormProps={entityFormProps}
        handleItemCreated={handleItemCreated}
        onRefetch={refetch}
        CreateFormOverride={customComponents?.createForm}
      />
      <EntityEditDeleteModals
        entityName={entityName}
        config={config}
        globalModalStack={globalModalStack}
        editingItem={editingItem}
        entityFormProps={entityFormProps}
        deleteMutation={deleteMutation}
        handleItemUpdated={handleItemUpdated}
        handleItemDeleted={handleItemDeleted}
        handleCancelDelete={handleCancelDelete}
      />
      {globalModalStack.isModalOpen("csv-import") && customComponents?.importModal && (
        <customComponents.importModal
          onSuccess={() => { globalModalStack.closeModal("csv-import"); refetch(); }}
          triggerButton={<div />}
        />
      )}
      {customComponents?.composeModal && (
        <customComponents.composeModal
          isOpen={showComposeModal}
          onOpenChange={(open: boolean) => !open && setShowComposeModal(false)}
        />
      )}
      {customComponents?.detailModal ? (
        <customComponents.detailModal modalId={`${entityName}-detail`} />
      ) : (
        <UniversalDetailModalContainer
          modalId={`${entityName}-detail`}
          entityName={entityName}
          config={config}
          hooks={hooks}
          itemIds={itemIds}
          currentItemIndex={currentItemIndex}
        />
      )}
      {globalModalStack.isModalOpen("ask-meridian-entity") && <AskMeridianModalContainer />}
      {/* ActionResolverModal now mounts globally in DetailPageModals so the
          manifest action system works on custom pages too (e.g. /sequences). */}
      {globalModalStack.isModalOpen("log-activity") && <LogActivityModal />}
      {globalModalStack.isModalOpen(MODAL_IDS.communications.reassignSalesAttribution) && (
        <ReassignCommunicationSalesAttributionModal />
      )}
      <SetDueDateModal />
      <ExportScopeModal
        isOpen={showExportModal}
        entityName={entityName}
        currentPageCount={currentPageCount}
        totalCount={totalCount}
        isGrouped={false}
        groupByField={undefined}
        dualGrouping={{ primary: null, secondary: null }}
        isLoading={isExporting}
        exportProgress={exportProgress}
        hasActiveFilters={hasActiveFilters}
        onExport={handleExportWithScope}
        onCancel={() => setShowExportModal(false)}
      />
      {(entityName === "companies" || entityName === "contacts") && (
        <ResearchCompanyModal contextContact={contextContact} />
      )}
      {(entityName === "companies" || entityName === "contacts") && (
        <FindDecisionMakersModal contextContact={contextContact} />
      )}
      {entityName === "contacts" && <LinkCompanyModalWrapper />}
      {entityName === "contacts" && <ContactConsolidationModalWrapper />}
      {entityName === "companies" && <LinkContactModalWrapper />}
      {entityName === "companies" && <CompanyConsolidationModalWrapper />}
      {(entityName === "contacts" || entityName === "companies") && globalModalStack.isModalOpen(`bulk-export-emails-${entityName}`) && (
        <BulkEmailExportModal />
      )}
      {entityName === "offerings" && globalModalStack.isModalOpen(MODAL_IDS.offerings.solutionEngineering) && (
        <SolutionEngineeringModal />
      )}
    </>
  );
}
